import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { extractErrorMessage, formatDateToISO, getWorkerConflictKey, type Locale } from "@/shared/utils";
import { usePagination } from "@/shared/hooks";
import type { SelectWithCheckboxOption } from "@/shared/ui";
import { useSyncUserRolesMutation } from "@/features/auth/api";
import { useGetWorkersQuery, useDownloadWorkerProfilePreviewMutation, useRehireWorkerMutation, useDeleteImportDraftMutation } from "../api";
import { STATUS_MAP } from "../consts/statuses";
import { getSocialCategoryIconConfig } from "../consts/socialCategoryIcons";
import { getSocialCategoryLabel } from "../consts/socialCategories";
import type { WorkerListItem, ImportWorkerDraftListItem, EmployeeStatus } from "../types";
import type { StatusOption } from "../pages/EmployeesPage/components/EmployeesFilter";
import { useEmployeesFilter, hasActiveEmployeesFilters } from "./useEmployeesFilter";
import {
  useEmployeesSort,
  toggleEmployeesSort,
  type EmployeesSortKey,
  type EmployeesSortConfig,
} from "./useEmployeesSort";

const SOCIAL_CATEGORY_TYPES = [
  "disability_1",
  "disability_2",
  "child_disability",
  "veteran",
  "orphan",
  "large_family",
  "single_parent_family",
  "kandas",
] as const;

export interface UseEmployeesListPageReturn {
  importDrafts: ImportWorkerDraftListItem[];
  isLoading: boolean;
  isError: boolean;
  search: string;
  setSearch: (value: string) => void;
  hireDateRange: {
    start: Date | null;
    end: Date | null;
  };
  setHireDateRange: (value: { start: Date | null; end: Date | null }) => void;
  statusFilter: EmployeeStatus | null;
  setStatusFilter: (value: EmployeeStatus | null) => void;
  statusOptions: StatusOption[];
  activeFilters: boolean;
  view: "table" | "cards";
  setView: (value: "table" | "cards") => void;
  locale: Locale;
  sortConfig: EmployeesSortConfig | null;
  handleSort: (key: EmployeesSortKey) => void;
  filteredWorkers: ReturnType<typeof useEmployeesFilter>;
  sortedWorkers: ReturnType<typeof useEmployeesSort>;
  pagination: ReturnType<typeof usePagination>;
  pageWorkers: WorkerListItem[];
  pageDrafts: ImportWorkerDraftListItem[];
  totalCount: number;
  handleResetFilters: () => void;
  handleDownloadProfile: (id: number) => Promise<void>;
  isDownloadingProfile: boolean;
  downloadingId: number | null;
  prompt: { title: string; text: string; variant?: "success" | "error" } | null;
  setPrompt: (value: { title: string; text: string; variant?: "success" | "error" } | null) => void;
  assignRoleEmployee: WorkerListItem | null;
  setAssignRoleEmployee: (value: WorkerListItem | null) => void;
  confirmDeleteDraftId: number | null;
  setConfirmDeleteDraftId: (value: number | null) => void;
  handleDeleteDraftConfirm: (id: number) => Promise<void>;
  isDeletingDraft: boolean;
  t: ReturnType<typeof useTranslation>["t"];
  handleSyncRoles: (roles: string[]) => Promise<void>;
  isSyncingRoles: boolean;
  handleRehire: (worker: WorkerListItem) => Promise<void>;
  isRehiring: boolean;
  previewUrl: string | null;
  previewFileName: string | null;
  previewTitle: string;
  closePreview: () => void;
  stats: { totalEmployees: number; activeEmployees: number; totalSalary: number };
  socialCategoryFilter: string[];
  setSocialCategoryFilter: (value: string[]) => void;
  socialCategoryOptions: SelectWithCheckboxOption<string>[];
}

export function useEmployeesListPage(): UseEmployeesListPageReturn {
  const { i18n, t } = useTranslation("EmployeesPage");
  const { t: tCommon } = useTranslation("Common");
  const locale = i18n.language as Locale;

  const [search, setSearch] = useState("");
  const [hireDateRange, setHireDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });

  const [statusFilter, setStatusFilter] = useState<EmployeeStatus | null>(null);
  const [socialCategoryFilter, setSocialCategoryFilter] = useState<string[]>([]);

  const statusOptions: StatusOption[] = useMemo(
    () => [
      { label: t("filters.allStatuses"), value: null },
      ...Object.entries(STATUS_MAP).map(([value, config]) => ({
        label: t(config.label),
        value: value as EmployeeStatus,
      })),
    ],
    [t],
  );

  const [view, setView] = useState<"table" | "cards">("table");
  const [sortConfig, setSortConfig] = useState<EmployeesSortConfig | null>(null);

  function handleSort(key: EmployeesSortKey) {
    setSortConfig(function toggleSort(prev) {
      return toggleEmployeesSort(prev, key);
    });
  }

  const {
    data,
    isLoading: isLoadingWorkers,
    isError,
  } = useGetWorkersQuery({
    status: statusFilter || undefined,
  });

  const isLoading = isLoadingWorkers;

  const [downloadProfile, { isLoading: isDownloadingProfile }] = useDownloadWorkerProfilePreviewMutation();
  const [syncRoles, { isLoading: isSyncingRoles }] = useSyncUserRolesMutation();
  const [rehireWorker, { isLoading: isRehiring }] = useRehireWorkerMutation();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);
  const [assignRoleEmployee, setAssignRoleEmployee] = useState<WorkerListItem | null>(null);
  const [deleteImportDraft, { isLoading: isDeletingDraft }] = useDeleteImportDraftMutation();
  const [confirmDeleteDraftId, setConfirmDeleteDraftId] = useState<number | null>(null);

  const filterState = useMemo(
    () => ({
      search,
      hireDateRange,
      statusFilter,
      socialCategoryFilter,
    }),
    [search, hireDateRange, statusFilter, socialCategoryFilter],
  );

  const workers = data?.workers;
  const importDrafts = data?.import_drafts ?? [];

  const filteredWorkers = useEmployeesFilter(workers, filterState);
  const sortedWorkers = useEmployeesSort(filteredWorkers, sortConfig);

  const totalCount = importDrafts.length + sortedWorkers.length;
  const pagination = usePagination(totalCount, 10, [
    search,
    hireDateRange,
    statusFilter,
    socialCategoryFilter,
  ]);
  const draftCount = importDrafts.length;
  const pageDrafts = importDrafts.slice(
    Math.min(pagination.startIndex, draftCount),
    Math.min(pagination.endIndex, draftCount),
  );
  const pageWorkers = sortedWorkers.slice(
    Math.max(0, pagination.startIndex - draftCount),
    Math.max(0, pagination.endIndex - draftCount),
  );

  const activeFilters = hasActiveEmployeesFilters(filterState);

  function handleResetFilters() {
    setSearch("");
    setHireDateRange({ start: null, end: null });
    setStatusFilter(null);
    setSocialCategoryFilter([]);
    pagination.setPage(1);
  }

  function closePreview() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewFileName(null);
  }

  async function handleDownloadProfile(id: number) {
    if (isDownloadingProfile || downloadingId !== null) return;

    setDownloadingId(id);
    try {
      const { blob, filename } = await downloadProfile(id).unwrap();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewFileName(filename || t("downloads.employeeProfileFileName", { id }));
      setDownloadingId(null);
    } catch (error) {
      setDownloadingId(null);
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "messages.downloadFailed", t),
        variant: "error",
      });
    }
  }

  async function handleSyncRoles(roles: string[]) {
    if (!assignRoleEmployee?.user_id) return;
    try {
      const response = await syncRoles({ userId: assignRoleEmployee.user_id, roles }).unwrap();

      const addedCount = response.added.length;
      const removedCount = response.removed.length;

      let message = "";
      if (addedCount > 0 && removedCount > 0) {
        message = t("roleAssignment.success.updated", { added: addedCount, removed: removedCount });
      } else if (addedCount > 0) {
        message = t("roleAssignment.success.assignedSubtext");
      } else if (removedCount > 0) {
        message = t("roleAssignment.success.removedSubtext");
      } else {
        message = t("roleAssignment.success.noChanges");
      }

      setPrompt({
        title: t("roleAssignment.success.assigned"),
        text: message,
        variant: "success",
      });

      setAssignRoleEmployee(null);
    } catch (error) {
      const errorMessage =
        (error as { status?: number })?.status === 403
          ? t("roleAssignment.error.permissionDenied")
          : extractErrorMessage(error, "roleAssignment.error.assignFailed", t);
      setPrompt({
        title: t("messages.errorTitle"),
        text: errorMessage,
        variant: "error",
      });
      setAssignRoleEmployee(null);
    }
  }

  async function handleRehire(worker: WorkerListItem) {
    if (isRehiring) return;
    try {
      // Local calendar day, not the UTC one: toISOString() before 05:00 Almaty
      // time still reports yesterday, and this date lands on the contract.
      const today = formatDateToISO(new Date());
      await rehireWorker({ iin: worker.iin, start_date: today, sign_date: today }).unwrap();
      setPrompt({
        title: t("rehire.successTitle"),
        text: t("rehire.successText"),
        variant: "success",
      });
    } catch (error) {
      const conflictKey = getWorkerConflictKey(error);
      setPrompt({
        title: t("messages.errorTitle"),
        text: conflictKey ? tCommon(conflictKey) : extractErrorMessage(error, "rehire.error", t),
        variant: "error",
      });
    }
  }

  async function handleDeleteDraftConfirm(id: number) {
    if (isDeletingDraft) return;
    try {
      await deleteImportDraft(id).unwrap();
      setPrompt({
        title: t("draft.deleteSuccessTitle"),
        text: t("draft.deleteSuccess"),
        variant: "success",
      });
    } catch (error) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "draft.deleteError", t),
        variant: "error",
      });
    } finally {
      setConfirmDeleteDraftId(null);
    }
  }

  const socialCategoryCounts = useMemo(function computeSocialCategoryCounts() {
    const counts: Record<string, number> = {};
    const allWorkers = workers ?? [];
    for (const worker of allWorkers) {
      for (const cat of worker.social_categories) {
        counts[cat.category_type] = (counts[cat.category_type] || 0) + 1;
      }
    }
    return counts;
  }, [workers]);

  const socialCategoryOptions: SelectWithCheckboxOption<string>[] = useMemo(function buildSocialCategoryOptions() {
    return SOCIAL_CATEGORY_TYPES.map(function mapCategory(type) {
      const iconConfig = getSocialCategoryIconConfig(type);
      return {
        label: getSocialCategoryLabel(type, t),
        value: type,
        icon: iconConfig ? (
          <span className={iconConfig.colorClass}>
            <iconConfig.Icon size={16} color="currentColor" variant="Bold" />
          </span>
        ) : undefined,
        count: socialCategoryCounts[type] || 0,
      };
    });
  }, [t, socialCategoryCounts]);

  const stats = useMemo(function computeStats() {
    const employees = workers || [];
    const totalEmployees = employees.length;
    const activeEmployees = employees.filter(function filterActive(emp: WorkerListItem) {
      return emp.status === "active";
    }).length;
    const totalSalary = employees.reduce(function sumSalary(sum: number, emp: WorkerListItem) {
      const salary = emp.active_contract?.salary_amount;
      if (salary) {
        const numSalary = typeof salary === "string" ? parseFloat(salary) : salary;
        return sum + (isNaN(numSalary) ? 0 : numSalary);
      }
      return sum;
    }, 0);
    return { totalEmployees, activeEmployees, totalSalary: Math.round(totalSalary) };
  }, [workers]);

  return {
    importDrafts,
    isLoading,
    isError,
    search,
    setSearch,
    hireDateRange,
    setHireDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    view,
    setView,
    locale,
    sortConfig,
    handleSort,
    filteredWorkers,
    sortedWorkers,
    pagination,
    pageWorkers,
    pageDrafts,
    totalCount,
    handleResetFilters,
    handleDownloadProfile,
    isDownloadingProfile,
    downloadingId,
    prompt,
    setPrompt,
    assignRoleEmployee,
    setAssignRoleEmployee,
    confirmDeleteDraftId,
    setConfirmDeleteDraftId,
    handleDeleteDraftConfirm,
    isDeletingDraft,
    t,
    handleSyncRoles,
    isSyncingRoles,
    handleRehire,
    isRehiring,
    previewUrl,
    previewFileName,
    previewTitle: t("downloads.employeeProfileTitle"),
    closePreview,
    stats,
    socialCategoryFilter,
    setSocialCategoryFilter,
    socialCategoryOptions,
  };
}
